import React, { useMemo, useState } from 'react';
import { Building2, Check, ChevronDown, Search, ShieldCheck, Smartphone, Sparkles, X } from 'lucide-react';
import { Skeleton } from '../../components/ui/Skeleton';
import { sortModelIdsByPopularity } from '../../constants/chatImageModels';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';
import {
  PERSONAL_PLAN_ORDER,
  formatDiscountPercent,
  hasActivePackageDiscount,
  resolvePlanAction,
  upgradePayableToman
} from './pricingConstants';

const PERSONAL_ORDER = PERSONAL_PLAN_ORDER;
const MODEL_PREVIEW_COUNT = 12;

const PLAN_HIGHLIGHTS = {
  echo: ['pricingBulletEcho1', 'pricingBulletEcho2', 'pricingBulletEcho3', 'pricingBulletEcho4', 'pricingBulletEcho5'],
  plus: ['pricingBulletPlus1', 'pricingBulletPlus2', 'pricingBulletPlus3', 'pricingBulletPlus4', 'pricingBulletPlus5', 'pricingBulletPlus6'],
  pro: ['pricingBulletPro1', 'pricingBulletPro2', 'pricingBulletPro3', 'pricingBulletPro4', 'pricingBulletPro5', 'pricingBulletPro6'],
  promax: ['pricingBulletProMax1', 'pricingBulletProMax2', 'pricingBulletProMax3', 'pricingBulletProMax4', 'pricingBulletProMax5'],
  'org-pro': ['pricingBulletPro1', 'pricingBulletPro2', 'pricingBulletPro3', 'pricingBulletPro4', 'pricingBulletOrgMembers']
};

const PLAN_TAG_KEYS = {
  echo: 'pricingPlanTagEcho',
  plus: 'pricingPlanTagPlus',
  pro: 'pricingPlanTagPro',
  promax: 'pricingPlanTagProMax',
  'org-pro': 'pricingPlanTagOrg'
};

const COMPARISON_SECTIONS = [
  {
    titleKey: 'pricingCompareKeyDifferences',
    rows: [
      { labelKey: 'pricingCompareMessages', type: 'text', field: 'messagesPer3Hours' },
      { labelKey: 'pricingCompareProduct', type: 'flag', field: 'canCreateProduct' },
      { labelKey: 'pricingComparePublishes', type: 'publishes' },
      { labelKey: 'pricingCompareVideo', type: 'video', comingSoon: true },
      { labelKey: 'pricingCompareGamma', type: 'flag', field: 'canGamma', comingSoon: true },
      { labelKey: 'pricingCompareSuno', type: 'flag', field: 'canSuno', comingSoon: true },
      { labelKey: 'pricingCompareResearch', type: 'research' }
    ]
  },
  {
    titleKey: 'pricingCompareFeatures',
    rows: [
      { labelKey: 'pricingCompareGpt56', type: 'always' },
      { labelKey: 'pricingCompareImage', type: 'always' },
      { labelKey: 'pricingCompareFiles', type: 'files' },
      { labelKey: 'pricingCompareWebSearch', type: 'always' },
      { labelKey: 'pricingCompareVoice', type: 'flag', field: 'canVoice', comingSoon: true },
      { labelKey: 'pricingCompareVision', type: 'always' },
      { labelKey: 'pricingCompareArtifacts', type: 'flag', field: 'canArtifacts', comingSoon: true }
    ]
  }
];

const CellMark = ({ ok, inverted }) =>
  ok ? (
    <span
      className={classNames(
        'mx-auto inline-flex h-7 w-7 items-center justify-center rounded-full',
        inverted ? 'bg-white/15 text-white dark:bg-panel-strong/10 dark:text-text' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
      )}
    >
      <Check size={15} strokeWidth={2.6} aria-hidden />
    </span>
  ) : (
    <span className="mx-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-muted dark:bg-accent-soft dark:text-muted">
      <X size={14} strokeWidth={2.4} aria-hidden />
    </span>
  );

const cellValue = (pkg, row, t, formatNumber) => {
  if (!pkg) {
    return '—';
  }

  if (row.type === 'always') {
    return <CellMark ok />;
  }

  if (row.type === 'flag') {
    return <CellMark ok={Boolean(pkg[row.field])} />;
  }

  if (row.type === 'text') {
    return <span className="font-semibold tabular-nums">{formatNumber(pkg[row.field] ?? 0)}</span>;
  }

  if (row.type === 'files') {
    return pkg.filesPerDay < 0 ? <CellMark ok /> : t('packageFilesPerDay', { count: formatNumber(pkg.filesPerDay) });
  }

  if (row.type === 'publishes') {
    const slots = Number(pkg.maxActivePublishes ?? 0);
    if (slots <= 0) {
      return <CellMark ok={false} />;
    }
    return t('packagePublishSlots', { count: formatNumber(slots) });
  }

  if (row.type === 'video') {
    if (pkg.slug === 'pro') {
      return t('pricingVideoPerWeek', { count: formatNumber(10) });
    }
    if (pkg.slug === 'promax') {
      return t('pricingVideoPerWeek', { count: formatNumber(100) });
    }
    return <CellMark ok={false} />;
  }

  if (row.type === 'research') {
    const level = (pkg.deepResearchLevel || '').toLowerCase();
    if (level === 'full') {
      return <CellMark ok />;
    }
    if (level === 'limited') {
      return t('pricingResearchLimited');
    }
    return <CellMark ok={false} />;
  }

  return '—';
};

const modelIdsOf = (pkg) => new Set((pkg?.models || []).map((model) => model.modelId));

const planCardClass = (slug, featured) => {
  if (slug === 'promax') {
    return 'border-neutral-900 bg-accent text-white shadow-floating dark:border-white dark:bg-accent dark:text-text';
  }

  if (featured) {
    return 'border-neutral-950 bg-white shadow-floating ring-1 ring-neutral-950 dark:border-white dark:bg-panel-strong dark:ring-white';
  }

  return 'border-border bg-panel-strong dark:border-border dark:bg-panel-strong/80';
};

const PlanCard = ({ pkg, featured, wide, showCompareLink, buyingId, onBuy, language, t, formatNumber, formatThousands, entitlement }) => {
  const name = language === 'fa' ? pkg.nameFa : pkg.nameEn;
  const bullets = PLAN_HIGHLIGHTS[pkg.slug] || PLAN_HIGHLIGHTS.echo;
  const inverted = pkg.slug === 'promax';
  const tagKey = PLAN_TAG_KEYS[pkg.slug];
  const action = resolvePlanAction(pkg, entitlement);
  const credit = Number(entitlement?.remainingCreditToman || 0);
  const hasDiscount = hasActivePackageDiscount(pkg);
  const afterDiscount = Number(pkg.payableToman || 0);
  const payable = action.type === 'upgrade' ? upgradePayableToman(pkg, entitlement) : afterDiscount;
  const heroPrice = hasDiscount ? afterDiscount : payable;
  const showCredit = action.type === 'upgrade' && credit > 0;
  const discountLabel = t('pricingDiscountPercent', {
    percent: formatNumber(formatDiscountPercent(pkg.discountPercent))
  });
  const remainingDays = entitlement?.remainingDays;
  const buttonLabel =
    action.type === 'current'
      ? t('pricingCurrentPlan')
      : action.type === 'disabled'
        ? t('pricingDowngradeDisabled')
        : action.type === 'upgrade'
          ? t('pricingUpgradeNamed', { name })
          : t('pricingBuyNamed', { name });

  return (
    <article
      className={classNames(
        'relative flex snap-center flex-col rounded-[1.75rem] border p-6 transition duration-200',
        action.disabled ? '' : 'motion-safe:hover:-translate-y-1',
        wide ? 'mx-auto w-full max-w-xl' : 'min-w-[min(100%,18.5rem)] xl:min-w-0',
        planCardClass(pkg.slug, featured)
      )}
    >
      {featured ? (
        <span className="absolute -top-3 start-5 inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[11px] font-semibold text-white dark:bg-accent dark:text-text">
          <Sparkles size={12} strokeWidth={2.2} aria-hidden />
          {t('pricingPopular')}
        </span>
      ) : null}

      <div className="pt-1">
        <h2 className="text-xl font-black tracking-tight">{name}</h2>
        {tagKey ? (
          <p className={classNames('mt-1.5 text-sm leading-6', inverted ? 'text-white/80' : 'text-muted')}>
            {t(tagKey)}
          </p>
        ) : null}

        <div className="mt-5 border-t border-current/10 pt-4">
          {hasDiscount ? (
            <div className="mb-2.5 flex flex-wrap items-center gap-2">
              <span className={classNames('text-sm font-semibold line-through tabular-nums', inverted ? 'text-white/65' : 'text-muted')}>
                {formatThousands(pkg.priceToman)} {t('pricingPriceCurrency')}
              </span>
              <span
                className={classNames(
                  'inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold',
                  inverted
                    ? 'bg-white/15 text-white dark:bg-panel-strong/10 dark:text-text'
                    : 'bg-rose-500/10 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
                )}
              >
                {discountLabel}
              </span>
            </div>
          ) : null}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[2.35rem] font-black leading-none tracking-tight tabular-nums">
              {formatThousands(heroPrice)}
            </span>
            <span className={classNames('text-base font-bold', inverted ? 'text-white' : 'text-text')}>
              {t('pricingPriceCurrency')}
            </span>
          </div>
          {hasDiscount ? (
            <p className={classNames('mt-1.5 text-xs font-semibold', inverted ? 'text-white/80' : 'text-muted')}>
              {t('pricingPriceAfterDiscount')}
            </p>
          ) : null}
          {showCredit && !hasDiscount ? (
            <p className={classNames('mt-1 text-sm line-through tabular-nums', inverted ? 'text-white/70' : 'text-muted')}>
              {formatThousands(pkg.payableToman)} {t('pricingPriceCurrency')}
            </p>
          ) : null}
          <p className={classNames(hasDiscount ? 'mt-1' : 'mt-2', 'text-sm font-semibold', inverted ? 'text-white' : 'text-text')}>
            {t('pricingPricePeriod')}
          </p>
          <p className={classNames('mt-0.5 text-xs leading-5', inverted ? 'text-white/75' : 'text-muted')}>
            {t('pricingPriceVatNote')}
          </p>
        </div>
      </div>

      {showCredit ? (
        <div className={classNames('mt-4 rounded-2xl px-3 py-2.5 text-start text-[12px] leading-6', inverted ? 'bg-white/10 text-white/80 dark:bg-panel-strong/8 dark:text-muted' : 'bg-accent-soft/60 text-muted dark:bg-accent-soft dark:text-muted')}>
          <p>
            {t('pricingRemainingCredit', {
              days: remainingDays ?? 0,
              credit: formatThousands(credit)
            })}
          </p>
          <p className="font-semibold">
            {t('pricingUpgradePayable', { amount: formatThousands(payable) })}
          </p>
        </div>
      ) : action.type === 'current' && remainingDays != null ? (
        <p className={classNames('mt-4 text-start text-[12px] leading-6', inverted ? 'text-white/70 dark:text-muted' : 'text-muted')}>
          {t('pricingRemainingCredit', {
            days: remainingDays,
            credit: formatThousands(credit)
          })}
        </p>
      ) : null}

      <button
        type="button"
        disabled={action.disabled || buyingId === pkg.id}
        onClick={() => onBuy(pkg, false)}
        className={classNames(
          'mt-6 inline-flex min-h-12 w-full items-center justify-center rounded-2xl text-sm font-bold transition duration-150 disabled:cursor-not-allowed disabled:opacity-50',
          action.disabled
            ? inverted
              ? 'bg-white/15 text-white/70 dark:bg-panel-strong/10 dark:text-muted'
              : 'bg-neutral-200 text-muted dark:bg-accent-soft dark:text-muted'
            : inverted
              ? 'cursor-pointer bg-white text-text hover:bg-accent-soft dark:bg-panel-strong dark:text-text dark:hover:opacity-95'
            : featured
              ? 'cursor-pointer bg-accent text-white hover:opacity-95 dark:bg-accent dark:text-text dark:hover:opacity-95'
              : 'cursor-pointer bg-accent text-white hover:opacity-95 dark:bg-accent dark:text-text dark:hover:opacity-95'
        )}
      >
        {buyingId === pkg.id ? t('loading') : buttonLabel}
      </button>

      <ul className={classNames('mt-6 space-y-3 text-[13px] leading-6', inverted ? 'text-white/85 dark:text-text' : 'text-text')}>
        {bullets.map((key) => (
          <li key={key} className="flex items-start gap-2.5">
            <span
              className={classNames(
                'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full',
                inverted ? 'bg-white/12 text-white dark:bg-panel-strong/10 dark:text-text' : 'bg-accent-soft text-text dark:bg-accent-soft dark:text-text'
              )}
            >
              <Check size={12} strokeWidth={2.8} aria-hidden />
            </span>
            <span>{t(key)}</span>
          </li>
        ))}
      </ul>

      {showCompareLink ? (
        <button
          type="button"
          onClick={() => document.getElementById('pricing-compare')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className={classNames(
            'mt-auto cursor-pointer pt-6 text-start text-sm font-semibold underline-offset-4 hover:underline',
            inverted ? 'text-white/80 dark:text-muted' : 'text-muted hover:text-text dark:hover:text-text'
          )}
        >
          {t('pricingSeeMore')}
        </button>
      ) : (
        <div className="mt-auto" />
      )}
    </article>
  );
};

export const PricingPlans = ({ packages, loading, buyingId, onBuy, entitlement }) => {
  const { t, language, formatNumber, locale } = useI18n();
  const { models: catalogModels } = useLlmCatalog();
  const [kind, setKind] = useState('personal');
  const [modelQuery, setModelQuery] = useState('');
  const [modelPackageSlug, setModelPackageSlug] = useState(null);
  const [modelSupportFilter, setModelSupportFilter] = useState('all');
  const [showAllModels, setShowAllModels] = useState(false);

  const personal = useMemo(
    () => PERSONAL_ORDER.map((slug) => packages.find((item) => item.slug === slug && item.kind === 'Personal')).filter(Boolean),
    [packages]
  );
  const orgPackages = useMemo(
    () => packages.filter((item) => item.kind === 'Organization'),
    [packages]
  );
  const visible = kind === 'personal' ? personal : orgPackages;

  const catalogNameById = useMemo(() => {
    const map = new Map();
    catalogModels.forEach((model) => {
      if (model?.key) {
        map.set(model.key, model.name || model.key);
      }
    });
    return map;
  }, [catalogModels]);

  const comparedModelIds = useMemo(() => {
    const ids = new Set();
    personal.forEach((pkg) => {
      (pkg.models || []).forEach((model) => ids.add(model.modelId));
    });
    return sortModelIdsByPopularity([...ids], catalogModels);
  }, [catalogModels, personal]);

  const packageModelSets = useMemo(() => {
    const map = new Map();
    personal.forEach((pkg) => {
      map.set(pkg.slug, modelIdsOf(pkg));
    });
    return map;
  }, [personal]);

  const filteredModelIds = useMemo(() => {
    const query = modelQuery.trim().toLowerCase();
    const supported = modelPackageSlug ? packageModelSets.get(modelPackageSlug) : null;
    return comparedModelIds.filter((id) => {
      if (supported && modelSupportFilter === 'active' && !supported.has(id)) {
        return false;
      }
      if (supported && modelSupportFilter === 'inactive' && supported.has(id)) {
        return false;
      }
      if (!query) {
        return true;
      }
      const name = (catalogNameById.get(id) || '').toLowerCase();
      return id.toLowerCase().includes(query) || name.includes(query);
    });
  }, [catalogNameById, comparedModelIds, modelPackageSlug, modelQuery, modelSupportFilter, packageModelSets]);

  const visibleModelIds = modelQuery.trim() || showAllModels
    ? filteredModelIds
    : filteredModelIds.slice(0, MODEL_PREVIEW_COUNT);
  const hiddenModelCount = Math.max(0, filteredModelIds.length - visibleModelIds.length);

  const formatThousands = (toman) =>
    formatNumber(new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(Number(toman || 0) / 1000)));

  const applyPackageSupportFilter = (slug, nextFilter) => {
    const sameSelection = modelPackageSlug === slug && modelSupportFilter === nextFilter;
    if (nextFilter === 'all' || sameSelection) {
      setModelPackageSlug(null);
      setModelSupportFilter('all');
    } else {
      setModelPackageSlug(slug);
      setModelSupportFilter(nextFilter);
    }
    setShowAllModels(false);
  };

  const supportFilterOptions = [
    { id: 'all', label: t('pricingCompareFilterAll') },
    { id: 'active', label: t('pricingCompareFilterActive') },
    { id: 'inactive', label: t('pricingCompareFilterInactive') }
  ];

  return (
    <div>
      <div className="relative mx-auto max-w-3xl text-center">
        <span className="inline-flex rounded-full border border-border bg-panel px-3.5 py-1.5 text-xs font-bold text-muted dark:border-border dark:bg-panel-strong/80">
          {t('pricingEyebrow')}
        </span>
        <h1 className="mt-5 text-4xl font-black tracking-[-0.05em] text-text sm:text-5xl">
          {t('pricingTitle')}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-muted sm:text-base">
          {t('pricingPackagesLead')}
        </p>
        {entitlement?.hasPackage ? (
          <p className="mx-auto mt-3 max-w-2xl text-sm font-medium leading-7 text-text">
            {t('pricingUpgradeCreditLead')}
          </p>
        ) : null}
      </div>

      <div className="mx-auto mt-8 flex w-fit rounded-full border border-border bg-panel p-1 shadow-sm dark:border-border dark:bg-panel-strong">
        {[
          { id: 'personal', label: t('pricingPersonalTab'), icon: Sparkles },
          { id: 'org', label: t('pricingOrgTab'), icon: Building2 }
        ].map((tab) => {
          const Icon = tab.icon;
          const active = kind === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setKind(tab.id)}
              className={classNames(
                'inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-full px-4 text-sm font-semibold transition duration-150',
                active
                  ? 'bg-accent text-white dark:bg-accent dark:text-text'
                  : 'text-muted hover:text-text dark:hover:text-text'
              )}
            >
              <Icon size={15} strokeWidth={2.2} aria-hidden />
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-[28rem] w-full rounded-[1.75rem]" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-10 rounded-[1.75rem] border border-dashed border-border px-6 py-16 text-center text-sm text-muted dark:border-border">
          {t('pricingPlansEmpty')}
        </div>
      ) : (
        <div
          className={classNames(
            'mt-10 flex gap-4 overflow-x-auto pt-3 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] snap-x snap-mandatory [&::-webkit-scrollbar]:hidden',
            kind === 'org' ? 'xl:grid xl:grid-cols-1 xl:overflow-visible' : 'xl:grid xl:grid-cols-4 xl:overflow-visible'
          )}
        >
          {visible.map((pkg) => (
            <PlanCard
              key={pkg.id}
              pkg={pkg}
              featured={pkg.slug === 'plus' || kind === 'org'}
              wide={kind === 'org'}
              showCompareLink={kind === 'personal'}
              buyingId={buyingId}
              onBuy={onBuy}
              language={language}
              t={t}
              formatNumber={formatNumber}
              formatThousands={formatThousands}
              entitlement={entitlement}
            />
          ))}
        </div>
      )}

      <div className="mx-auto mt-10 grid max-w-3xl gap-3 sm:grid-cols-2">
        <div className="flex items-start gap-3 rounded-2xl border border-border bg-panel px-4 py-3.5 dark:border-border dark:bg-panel-strong/70">
          <ShieldCheck size={18} className="mt-0.5 shrink-0 text-text" strokeWidth={2.1} aria-hidden />
          <p className="text-xs leading-6 text-muted">{t('pricingGuarantee')}</p>
        </div>
        <div className="flex items-start gap-3 rounded-2xl border border-border bg-panel px-4 py-3.5 dark:border-border dark:bg-panel-strong/70">
          <Smartphone size={18} className="mt-0.5 shrink-0 text-text" strokeWidth={2.1} aria-hidden />
          <p className="text-xs leading-6 text-muted">{t('pricingDeviceLimit')}</p>
        </div>
      </div>

      {kind === 'org' ? (
        <p className="mx-auto mt-6 max-w-2xl text-center text-sm leading-7 text-muted">{t('pricingOrgLead')}</p>
      ) : null}

      {kind === 'personal' && personal.length > 0 ? (
        <section id="pricing-compare" className="mt-20 scroll-mt-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-black tracking-[-0.04em] text-text">{t('pricingCompareTitle')}</h2>
            <p className="mt-3 text-sm leading-7 text-muted">{t('pricingCompareLead')}</p>
          </div>

          <div className="mt-8 overflow-hidden rounded-[1.75rem] border border-border bg-white shadow-sm dark:border-border dark:bg-panel-strong">
            <div className="overflow-x-auto">
              <table className="min-w-[44rem] w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="sticky start-0 z-10 bg-white px-5 py-4 text-start font-semibold text-muted dark:bg-panel-strong dark:text-muted">
                      {t('pricingCompareFeaturesCol')}
                    </th>
                    {personal.map((pkg) => {
                      const featured = pkg.slug === 'plus';
                      return (
                        <th
                          key={pkg.id}
                          className={classNames(
                            'px-3 py-4 text-center font-black tracking-tight',
                            featured ? 'bg-accent-soft/60 text-text dark:bg-accent-soft dark:text-text' : 'text-text'
                          )}
                        >
                          <span className="block">{language === 'fa' ? pkg.nameFa : pkg.nameEn}</span>
                          {hasActivePackageDiscount(pkg) ? (
                            <span className="mt-1.5 block text-[11px] font-semibold leading-5">
                              <span className="block text-[10px] font-medium line-through text-muted">
                                {formatThousands(pkg.priceToman)} {t('pricingPriceCurrency')}
                              </span>
                              <span className="tabular-nums">{formatThousands(pkg.payableToman)}</span>
                              <span className="ms-1 text-[10px] font-bold text-rose-600 dark:text-rose-300">
                                {t('pricingDiscountPercent', {
                                  percent: formatNumber(formatDiscountPercent(pkg.discountPercent))
                                })}
                              </span>
                            </span>
                          ) : null}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_SECTIONS.map((section) => (
                    <React.Fragment key={section.titleKey}>
                      <tr>
                        <td
                          colSpan={personal.length + 1}
                          className="bg-accent-soft/60 px-5 py-2.5 text-start text-[11px] font-bold uppercase tracking-[0.14em] text-muted dark:bg-accent-soft"
                        >
                          {t(section.titleKey)}
                        </td>
                      </tr>
                      {section.rows.map((row) => (
                        <tr key={row.labelKey} className="border-t border-border/70">
                          <td className="sticky start-0 z-10 bg-white px-5 py-3.5 text-start text-text dark:bg-panel-strong dark:text-muted">
                            <span className="inline-flex flex-wrap items-center gap-2">
                              {t(row.labelKey)}
                              {row.comingSoon ? (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-400/15 dark:text-amber-200">
                                  {t('pricingComingSoon')}
                                </span>
                              ) : null}
                            </span>
                          </td>
                          {personal.map((pkg) => (
                            <td
                              key={`${pkg.id}-${row.labelKey}`}
                              className={classNames(
                                'px-3 py-3.5 text-center text-text',
                                pkg.slug === 'plus' ? 'bg-accent-soft/70/50' : ''
                              )}
                            >
                              {cellValue(pkg, row, t, formatNumber)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr>
                    <td colSpan={personal.length + 1} className="bg-accent-soft/60 px-5 py-4 dark:bg-accent-soft">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
                          {t('pricingCompareModels', { count: formatNumber(filteredModelIds.length) })}
                        </p>
                        <label className="relative block lg:w-72">
                          <Search size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
                          <input
                            type="search"
                            value={modelQuery}
                            onChange={(event) => {
                              setModelQuery(event.target.value);
                              setShowAllModels(false);
                            }}
                            placeholder={t('pricingCompareSearch')}
                            className="h-10 w-full rounded-xl border border-border bg-white pe-3 ps-9 text-sm text-text outline-none transition focus:border-neutral-950 dark:border-border dark:bg-panel-strong dark:focus:border-white"
                          />
                        </label>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="sticky start-0 z-10 bg-accent-soft/60 px-5 py-3 text-start text-xs font-semibold text-muted dark:bg-accent-soft">
                      {t('pricingCompareFilterByPackage')}
                    </td>
                    {personal.map((pkg) => {
                      const selected = modelSupportFilter !== 'all' && modelPackageSlug === pkg.slug;
                      const current = selected ? modelSupportFilter : 'all';
                      return (
                        <td
                          key={`${pkg.id}-support-filter`}
                          className={classNames(
                            'px-2 py-2 text-center',
                            selected ? 'bg-accent-soft dark:bg-accent-soft' : 'bg-accent-soft/70/70'
                          )}
                        >
                          <label className="relative mx-auto block w-[7.5rem]">
                            <span className="sr-only">
                              {t('pricingCompareFilterByPackage')} {language === 'fa' ? pkg.nameFa : pkg.nameEn}
                            </span>
                            <select
                              value={current}
                              onChange={(event) => applyPackageSupportFilter(pkg.slug, event.target.value)}
                              className="h-8 w-full cursor-pointer appearance-none rounded-lg border border-border bg-white pe-7 ps-2 text-center text-[11px] font-semibold text-text outline-none transition focus:border-neutral-950 dark:border-border dark:bg-panel-strong dark:focus:border-white"
                            >
                              {supportFilterOptions.map((option) => (
                                <option key={option.id} value={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <ChevronDown
                              size={14}
                              className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 text-muted"
                              aria-hidden
                            />
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                  {comparedModelIds.length === 0 ? (
                    <tr>
                      <td colSpan={personal.length + 1} className="px-5 py-8 text-center text-sm text-muted">
                        {t('pricingCompareModelsEmpty')}
                      </td>
                    </tr>
                  ) : filteredModelIds.length === 0 ? (
                    <tr>
                      <td colSpan={personal.length + 1} className="px-5 py-8 text-center text-sm text-muted">
                        {t('pricingCompareModelsNoMatch')}
                      </td>
                    </tr>
                  ) : (
                    visibleModelIds.map((modelId) => (
                      <tr key={modelId} className="border-t border-border/70">
                        <td className="sticky start-0 z-10 bg-white px-5 py-3 text-start dark:bg-panel-strong">
                          <span className="block font-medium text-text">
                            {catalogNameById.get(modelId) || modelId}
                          </span>
                          {catalogNameById.get(modelId) && catalogNameById.get(modelId) !== modelId ? (
                            <span className="mt-0.5 block font-mono text-[11px] text-muted" dir="ltr">
                              {modelId}
                            </span>
                          ) : null}
                        </td>
                        {personal.map((pkg) => (
                          <td
                            key={`${pkg.id}-${modelId}`}
                            className={classNames(
                              'px-3 py-3 text-center',
                              pkg.slug === 'plus' || (modelSupportFilter !== 'all' && modelPackageSlug === pkg.slug)
                                ? 'bg-accent-soft/70/50'
                                : ''
                            )}
                          >
                            <CellMark ok={modelIdsOf(pkg).has(modelId)} />
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {hiddenModelCount > 0 ? (
              <div className="border-t border-border px-5 py-4 text-center dark:border-border">
                <button
                  type="button"
                  onClick={() => setShowAllModels(true)}
                  className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-text hover:text-text dark:text-muted dark:hover:text-text"
                >
                  {t('pricingShowAllModels', { count: formatNumber(filteredModelIds.length) })}
                  <ChevronDown size={16} aria-hidden />
                </button>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
};
